import React from 'react';

import { TFunction } from 'i18next';

import bgIntro from '../../assets/images/news-bg.jpg';
import { PATHWAY_STATUSES, VIDEO_STATUSES } from '../../types';
import { translate } from '../../utils/helpers';

export enum CellType {
  Array = 'array',
  Number = 'number',
  String = 'string',
  Date = 'date',
  Image = 'image',
  ImageAndTitle = 'image-title',
  Seconds = 'seconds',
  ViewDetails = 'view-details',
  Progress = 'progress',
  Link = 'link',
  Expand = 'expand',
  LabelWithHelperText = 'label-with-helper-text',
  Status = 'status',
}

export type TableHeadingDataType = {
  title: string;
  key: string;
  type: CellType;
  sortable?: boolean;
  sortingKey?: string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  render?: (value?: string | number) => JSX.Element | string;
  finishedStatusKey?: string;
};

const getPathwayTableCell = (t: TFunction, classes, val?: string | number) => {
  return (
    <span className={val === PATHWAY_STATUSES.COMPLETED ? classes.greenLabel : classes.grayLabel}>
      {t(`common.${val}` || '')}
    </span>
  );
};

const getVideoTableCell = (t: TFunction, classes, val?: string | number) => {
  return (
    <span className={val === VIDEO_STATUSES.COMPLETED ? classes.greenLabel : classes.grayLabel}>
      {t(`common.${val}` || '')}
    </span>
  );
};

export const USERS_TABLE_HEADING: TableHeadingDataType[] = [
  {
    title: translate('block.analyticsTable.learner'),
    key: 'name',
    type: CellType.String,
    sortingKey: 'display',
    minWidth: 150,
  },
  {
    title: translate('block.analyticsTable.team'),
    key: 'teams',
    type: CellType.Array,
    minWidth: 150,
  },
  {
    title: translate('block.analyticsTable.videosStarted'),
    key: 'videosStarted',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.videosCompleted'),
    key: 'videosCompleted',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.pathwaysWatched'),
    key: 'pathwaysWatched',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.pathwaysCompleted'),
    key: 'pathwaysCompleted',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.cpdEarned'),
    key: 'CPDearned',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.hoursWatched'),
    key: 'hoursWatched',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.loginSessions'),
    key: 'loginSessions',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.lastSessions'),
    key: 'lastSessions',
    type: CellType.Date,
    minWidth: 190,
    align: 'right',
  },
];

export const EXPORT_USERS_TABLE_HEADING: TableHeadingDataType[] = [
  USERS_TABLE_HEADING[0],
  {
    title: translate('block.analyticsTable.email'),
    key: 'email',
    type: CellType.String,
    minWidth: 150,
  },
  ...USERS_TABLE_HEADING.slice(1),
];

export const PATHWAYS_TABLE_HEADING: TableHeadingDataType[] = [
  {
    title: translate('block.analyticsTable.pathway'),
    key: 'pathway',
    type: CellType.ImageAndTitle,
    sortingKey: 'label',
    minWidth: 210,
  },
  {
    title: translate('block.analyticsTable.duration'),
    key: 'duration',
    type: CellType.Seconds,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.cpdAvailable'),
    key: 'cpdAvailable',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.usersStarted'),
    key: 'usersStarted',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.usersWatched'),
    key: 'usersWatched',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.usersCompleted'),
    key: 'usersCompleted',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: '',
    key: 'viewDetails',
    type: CellType.ViewDetails,
    sortable: false,
    minWidth: 160,
  },
];

export const getPathwaysAdvancedTableHeading = (t: TFunction, classes): TableHeadingDataType[] => [
  {
    title: t('block.analyticsTable.pathway'),
    key: 'pathwayName',
    type: CellType.String,
    minWidth: 210,
    sortable: false,
  },
  {
    title: t('block.analyticsTable.duration'),
    key: 'duration',
    type: CellType.Seconds,
    align: 'center',
    sortable: false,
  },
  {
    title: t('block.analyticsTable.learner'),
    key: 'learner',
    type: CellType.LabelWithHelperText,
    align: 'left',
    sortingKey: 'label',
    sortable: false,
  },
  {
    title: t('block.analyticsTable.progress'),
    key: 'progress',
    type: CellType.Progress,
    align: 'left',
    minWidth: 220,
    sortable: false,
  },
  {
    title: t('block.analyticsTable.status'),
    key: 'status',
    type: CellType.Status,
    align: 'left',
    render: (val) => getPathwayTableCell(t, classes, val),
    sortable: false,
  },
  {
    title: t('block.analyticsTable.testScore'),
    key: 'testScore',
    type: CellType.Number,
    align: 'left',
    minWidth: 112,
    render: (val) => (val || val === 0 ? `${val}%` : ''),
    sortable: false,
  },
];

export const pathwaysExportAdvancedTableHeading: TableHeadingDataType[] = [
  {
    title: translate('block.analyticsTable.pathway'),
    key: 'pathwayName',
    type: CellType.String,
    minWidth: 210,
  },
  {
    title: translate('block.analyticsTable.duration'),
    key: 'duration',
    type: CellType.Seconds,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.learner'),
    key: 'learnerName',
    type: CellType.String,
    align: 'left',
  },
  {
    title: translate('block.analyticsTable.email'),
    key: 'email',
    type: CellType.String,
    align: 'left',
    sortable: false,
  },
  {
    title: translate('block.analyticsTable.progress'),
    key: 'progress',
    type: CellType.Progress,
    align: 'left',
    minWidth: 220,
  },
  {
    title: translate('block.analyticsTable.status'),
    key: 'status',
    type: CellType.Status,
  },
  {
    title: translate('block.analyticsTable.testScore'),
    key: 'testScore',
    type: CellType.Number,
  },
];

export const VIDEOS_TABLE_HEADING: TableHeadingDataType[] = [
  {
    title: translate('block.analyticsTable.title'),
    key: 'video',
    type: CellType.ImageAndTitle,
    sortingKey: 'label',
    minWidth: 210,
  },
  {
    title: translate('block.analyticsTable.presenter'),
    key: 'presenter',
    type: CellType.String,
    minWidth: 150,
  },
  {
    title: translate('block.analyticsTable.category'),
    key: 'videoCategories',
    type: CellType.String,
    sortable: false,
  },
  {
    title: translate('block.analyticsTable.duration'),
    key: 'duration',
    type: CellType.Seconds,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.usersStarted'),
    key: 'usersStarted',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.usersCompleted'),
    key: 'usersCompleted',
    type: CellType.Number,
    align: 'center',
  },
  {
    title: '',
    key: 'viewDetails',
    type: CellType.ViewDetails,
    sortable: false,
    minWidth: 160,
  },
];

export const getVideosAdvancedTableHeading = (t: TFunction, classes): TableHeadingDataType[] => [
  {
    title: t('block.analyticsTable.title'),
    key: 'videoTitle',
    type: CellType.String,
    minWidth: 180,
    sortable: false,
  },
  {
    title: t('block.analyticsTable.presenter'),
    key: 'author',
    type: CellType.String,
    sortable: false,
  },
  {
    title: t('block.analyticsTable.category'),
    key: 'categories',
    type: CellType.String,
    sortable: false,
  },
  {
    title: t('block.analyticsTable.duration'),
    key: 'duration',
    type: CellType.Seconds,
    sortable: false,
    align: 'center',
  },
  {
    title: t('block.analyticsTable.learner'),
    key: 'learner',
    type: CellType.LabelWithHelperText,
    align: 'left',
    sortingKey: 'label',
    sortable: false,
    minWidth: 220,
  },
  {
    title: t('block.analyticsTable.progress'),
    key: 'progress',
    type: CellType.Progress,
    align: 'left',
    sortable: false,
    minWidth: 220,
  },
  {
    title: t('block.analyticsTable.status'),
    key: 'status',
    type: CellType.Status,
    align: 'left',
    sortable: false,
    render: (val) => getVideoTableCell(t, classes, val),
  },
];

export const videosExportAdvancedTableHeading: TableHeadingDataType[] = [
  {
    title: translate('block.analyticsTable.title'),
    key: 'videoTitle',
    type: CellType.String,
    minWidth: 180,
  },
  {
    title: translate('block.analyticsTable.presenter'),
    key: 'author',
    type: CellType.String,
  },
  {
    title: translate('block.analyticsTable.category'),
    key: 'categories',
    type: CellType.String,
  },
  {
    title: translate('block.analyticsTable.duration'),
    key: 'duration',
    type: CellType.Seconds,
    align: 'center',
  },
  {
    title: translate('block.analyticsTable.learner'),
    key: 'learnerName',
    type: CellType.String,
    align: 'left',
  },
  {
    title: translate('block.analyticsTable.email'),
    key: 'email',
    type: CellType.String,
    align: 'left',
    sortable: false,
  },
  {
    title: translate('block.analyticsTable.progress'),
    key: 'progress',
    type: CellType.Progress,
    align: 'left',
    minWidth: 220,
  },
  {
    title: translate('block.analyticsTable.status'),
    key: 'status',
    type: CellType.Status,
  },
];

export const INTRO_DATA = {
  preview_image_url: bgIntro,
  heading: translate('page.analytics.heading'),
};

export const CSV_TABLE_DATA = [
  [
    'name',
    'email',
    'teams',
    'videosStarted',
    'videosCompleted',
    'pathwaysWatched',
    'pathwaysCompleted',
    'CPDearned',
    'hoursWatched',
    'loginSessions',
    'lastSessions',
  ],
  ['pathway', 'duration', 'cpdAvailable', 'usersStarted', 'usersWatched', 'usersCompleted'],
  ['video', 'presenter', 'videoCategories', 'duration', 'usersStarted', 'usersCompleted'],
  ['pathwayName', 'duration', 'learnerName', 'email', 'progress', 'status', 'testScore'],
  ['videoTitle', 'author', 'categories', 'duration', 'learnerName', 'email', 'progress', 'status'],
];

export const RADAR_USER_LIMIT = 5;

export const initialTeamFilter = {
  value: 'all',
  display: translate('page.analytics.allTeams'),
};

export const initialUsersFilter = [
  {
    value: 'all',
    display: translate('page.analytics.allLearners'),
  },
];

export const initialDatesFilter = {
  value: 'all',
  display: translate('page.analytics.allTime'),
};

export const initialDatesUserFilter = {
  value: 'all',
  display: translate('timeFilters.all'),
};

export const dateFilterOptions = [
  {
    display: translate('timeFilters.all'),
    value: 'all',
  },
  {
    display: translate('timeFilters.lastMonth'),
    value: 'lastMonth',
  },
  {
    display: translate('timeFilters.lastThreeMonths'),
    value: 'lastThreeMonths',
  },
  {
    display: translate('timeFilters.thisYear'),
    value: 'thisYear',
  },
  {
    display: translate('timeFilters.previousYear'),
    value: 'previousYear',
  },
  {
    display: translate('timeFilters.custom'),
    value: 'custom',
  },
];

export enum GRAPH_TYPE {
  Hours = 'hours',
  Logins = 'logins',
  Videos = 'videos',
}

export type sortKeyType = {
  key: string;
  value: number | null;
} | null;

export type CellProps = {
  row?: any;
  k: string;
  styles?: any;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
};

export enum MANAGER_VIEW_TYPES {
  Analytic = 'analytic',
  VideoTable = 'video_table',
  PathwayTable = 'pathway_table',
}

export type UsersProgress = {
  id?: string | null;
  userName?: string | null;
  completed: boolean;
  status: string;
  progress: number;
};

export enum VideoProgressStatuses {
  Started = 'started',
  Completed = 'completed',
}

export enum PathwayProgressStatuses {
  Started = 'started',
  Watched = 'watched',
  Completed = 'completed',
}

export const ANALYTICS_ROWS_PER_PAGE = 'ANALYTICS_ROWS_PER_PAGE';
